/**
 * Стенд проверки канала. К игровому слою отношения не имеет: он существует,
 * чтобы убедиться, что связка клиент-сервер живая, переживает обрыв и умеет
 * договориться о состоянии после потери сообщений.
 *
 * Живёт в сайдбаре и потому свёрстан плотно: каждый блок — одна тема.
 */

import { bridge, useBridge, useCountdown, type ClientState } from './app.socket.js'
import { Action, ROW_GRID, Row } from './sidebar.blocks.js'

/**
 * Состояние канала словом и стеклом светодиода. Порядок веток важен: «недоступен»
 * проверяем раньше «подключаюсь», иначе долгий обрыв читался бы как первая попытка.
 */
function statusView(s: ClientState) {
  if (s.connected)
    return {
      word: 'connected',
      // Слово в тон стеклу: тот же зелёный, что в середине градиента.
      text: 'text-[#5be86f]',
      led: 'bg-radial-[circle_at_38%_32%] from-[#8cff83] via-[#5be86f] to-[#1e9f3f] shadow-[0_0_6px_rgba(91,232,111,0.9)]',
    }
  if (s.unreachable && !s.connected)
    return {
      word: 'unreachable',
      text: 'text-danger-l',
      led: 'bg-radial-[circle_at_38%_32%] from-[#ff7770] via-[#d6433d] to-[#7f1717] shadow-[0_0_6px_rgba(214,67,61,0.8)]',
    }
  if (s.retry)
    return {
      word: 'retrying',
      text: 'text-champ',
      led: 'bg-radial-[circle_at_38%_32%] from-[#ffe0a3] via-[#e4b875] to-[#9a6a2d] shadow-[0_0_6px_rgba(228,184,117,0.8)]',
    }
  if (s.connecting)
    return {
      word: 'connecting',
      text: 'text-champ',
      led: 'bg-radial-[circle_at_38%_32%] from-[#ffe0a3] via-[#e4b875] to-[#9a6a2d] shadow-[0_0_6px_rgba(228,184,117,0.8)]',
    }
  // Связи нет и её никто не ищет (свой обрыв из стенда): красное стекло, как у «unreachable».
  return {
    word: 'disconnected',
    text: 'text-danger-l',
    led: 'bg-radial-[circle_at_38%_32%] from-[#ff7770] via-[#d6433d] to-[#7f1717] shadow-[0_0_6px_rgba(214,67,61,0.8)]',
  }
}

/**
 * Состояние канала двумя строками, без собственного блока: он встраивается
 * наверх игрового блока.
 *
 * Остальное уехало в журнал. `id`, `recovered` и `reconnects` там были всегда —
 * в строках `net`; `heartbeat` пишется каналом `beat` на каждый тик движка;
 * `revision` теперь пишется при изменении. В панели эти числа занимали пять
 * строк, отвечая на вопрос «что сейчас», хотя ценность у них в том, *когда*
 * они изменились, а это и есть журнал.
 */
export function ConnectionRows() {
  const s = useBridge()
  const view = statusView(s)
  const left = useCountdown(s.retry?.at ?? null, null)

  return (
    <>
      <Row
        // Пока ждём следующую попытку, отсчёт — часть слова состояния, тем же текстом.
        label={
          <span className={view.text}>
            {s.retry
              ? `retrying in ${Math.max(0, Math.ceil((left ?? s.retry.at - Date.now()) / 1000))}s (${s.retry.attempt} of ${s.retry.of})`
              : view.word}
          </span>
        }
        action={
          <>
            {/* Замер уходит в журнал строкой `ping`, в панели его значение не держим. */}
            <Action onClick={() => bridge.ping()} tone="good" disabled={!s.connected}>
              ping
            </Action>
            {/* Одна кнопка на два состояния: пока связь есть — обрыв, иначе — подъём. */}
            {s.connected ? (
              <>
                {/* Имитация сбоя связи: весь ряд попыток 5, 15, 30 секунд и ошибка. */}
                <Action onClick={() => bridge.break()} tone="bad">
                  break
                </Action>
                <Action onClick={() => bridge.disconnect()} tone="bad">
                  disconnect
                </Action>
              </>
            ) : (
              <Action
                onClick={() => bridge.connect()}
                tone={s.retry ? 'warn' : 'good'}
                disabled={s.connecting}
              >
                connect
              </Action>
            )}
          </>
        }
        led={<span className={`h-2.5 w-2.5 shrink-0 rounded-full ${view.led}`} aria-hidden="true" />}
      />
      {s.unreachable && !s.connected && (
        <p className="mt-2 text-[11px] text-danger-l">Server unreachable. Press connect to try again.</p>
      )}
    </>
  )
}

/**
 * Подключённые вкладки: личность транспорта, не игры. Живёт в общей секции
 * людей, поэтому без своего блока и заголовка.
 */
export function ClientRows({ exclude = new Set<string>() }: { exclude?: ReadonlySet<string> }) {
  const s = useBridge()

  return (
    <>
      {(s.harness?.clients ?? [])
        .filter((c) => !exclude.has(c.id))
        .map((c) => (
          <li key={`tab:${c.id}`} className={`${ROW_GRID} py-1 font-mono text-[11px]`}>
            {/* Своя строка — шампанью: отдельной пометки не нужно. */}
            <span className={`min-w-0 truncate ${c.id === s.socketId ? 'text-champ' : 'text-steel-l'}`}>
              {c.name}
            </span>
            {/* Свой статус зрителя дополняет вкладку, которая не привязана к игроку. */}
            <span className="text-right text-path">{c.id === s.socketId ? (s.role ?? '') : ''}</span>
            {/* Действие в правой колонке, как у комнат и игроков. */}
            <span className="text-right">
              <Action onClick={() => bridge.eject(c.id)} tone="bad">
                eject
              </Action>
            </span>
          </li>
        ))}
    </>
  )
}

/**
 * Фильтры журнала: комнаты, о которых в нём есть строки, в порядке первого
 * появления — так вкладки не прыгают с каждой новой строкой. Фильтр живёт,
 * пока живут его строки: удалённая комната остаётся в журнале и в фильтрах,
 * уходит вместе с `clear`.
 */
export function logFilters(s: ClientState): string[] {
  const codes = new Set<string>()
  for (let i = s.log.length - 1; i >= 0; i--) {
    const code = s.log[i].roomCode
    if (code) codes.add(code)
  }
  return [...codes]
}

/** Фильтр журнала: «all» — все строки, код комнаты — только её. */
function LogFilters({
  selected,
  onSelect,
}: {
  selected: string | null
  onSelect: (code: string | null) => void
}) {
  const s = useBridge()
  const names = new Map((s.rooms ?? []).map((room) => [room.code, room.roomName]))
  const tab = (code: string | null, label: string, title: string) => (
    <button
      key={code ?? '*'}
      type="button"
      role="tab"
      aria-selected={selected === code}
      title={title}
      onClick={() => onSelect(code)}
      className={`shrink-0 cursor-pointer rounded px-1.5 py-0.5 ${selected === code ? 'bg-steel-d/30 text-text' : 'text-steel-l hover:text-text'}`}
    >
      {label}
    </button>
  )

  return (
    <nav
      role="tablist"
      aria-label="Log filter"
      className="flex min-w-0 flex-1 flex-wrap gap-1 font-mono text-[10px]"
    >
      {tab(null, 'ALL', 'all messages')}
      {logFilters(s).map((code) => tab(code, code, `${names.get(code) ?? 'deleted room'} (${code})`))}
    </nav>
  )
}

/** Журнал с фильтрами занимает остаточную высоту сайдбара. */
export function Log({
  roomCode,
  onSelectRoom,
}: {
  /** Фильтр: код комнаты или `null` — все строки. */
  roomCode: string | null
  onSelectRoom: (code: string | null) => void
}) {
  const s = useBridge()
  const lines = roomCode ? s.log.filter((line) => line.roomCode === roomCode) : s.log

  return (
    <section className="flex min-h-40 flex-1 flex-col px-4 py-3">
      {/* Фильтры слева (переносятся на следующие строки), очистка справа на базовой линии верхней строки. */}
      <div className="mb-2 flex shrink-0 items-baseline gap-2">
        <LogFilters selected={roomCode} onSelect={onSelectRoom} />
        <Action onClick={() => bridge.clearLog(roomCode ?? undefined)} disabled={!lines.length}>
          clear
        </Action>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded border border-steel-d/30 bg-deep/60 p-1.5">
        {/* Дорожка полосы прозрачная: на тёмной плашке журнала светлый трек резал глаз. */}
        <div
          className={`min-h-0 flex-1 overflow-y-auto font-mono text-[10px] leading-relaxed [scrollbar-color:var(--color-steel-d)_transparent] [scrollbar-width:thin] ${lines.length ? '' : 'flex items-center justify-center'}`}
        >
          {/* Пустой журнал: тусклая метка по центру, чтобы он не читался как сломанный. */}
          {!lines.length && <span className="text-steel-d">(empty)</span>}
          {lines.map((line) => (
            <div key={line.id} className="flex gap-1.5 py-px">
              <span className="shrink-0 text-steel-d">{line.iso.slice(11, 19)}</span>
              <span
                className={
                  line.channel === 'room'
                    ? 'shrink-0 text-champ'
                    : line.channel === 'net'
                      ? 'shrink-0 text-turq'
                      : line.channel === 'game'
                        ? 'shrink-0 text-path'
                        : 'shrink-0 text-steel-d'
                }
              >
                {line.channel}
              </span>
              <span className="min-w-0 break-words text-steel-l">
                {line.text}
                {line.repeat > 1 && <span className="ml-1 text-steel-d">×{line.repeat}</span>}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
